import { api } from './_apiInterface';
import { AuthenticationResponseJSON, RegistrationResponseJSON, PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import { localStorageHelper } from '..';
import { pointer, sse } from 'src/app-config';
import { typeConfigDoc, typePreachingGroup, typeUser } from '@monorepo/models';
import { unauthenticatedConfig } from '@monorepo/helpers';

class UserServices {
    private Base = pointer.user;
    //
    ChangeEmailByAdmin = async (userId: number, newEmailAddress: string, oldEmailAddress: string) => {
        const data = await api.SendRequest(`${this.Base}/email`, 'PATCH', { newEmailAddress, oldEmailAddress, userId });
        return data;
    }
    ChangeMyEmail = async (newEmail: string) => {
        const data = await api.SendRequest(`${this.Base}/token`, 'PUT', { newEmail });
        return data;
    }
    ChangeMyPassword = async (team: number, psw: string | null, newPsw: string, id: string | null) => {
        return api.SendRequest(`${this.Base}/token`, 'PUT', { team, psw, newPsw, id });
    }
    ChangePswOtherUser = async (email: string) => {
        const data = await api.SendRequest<{ newPassword: string; }>(`${this.Base}/token`, 'PATCH', { email });
        return data;
    }
    DeleteMyAccount = async () => {
        const data = await api.SendRequest(this.Base, 'DELETE', { myUser: true });
        if (data.success) {
            localStorageHelper.RemoveAuthToken();
            sse.reconnect();
        }
        return data;
    }
    Delete = async (userId: number) => {
        const data = await api.SendRequest(this.Base, 'DELETE', { userId });
        return data;
    }
    Edit = async (userId: number, isActive: boolean, roles: number[]) => {
        const data = await api.SendRequest(this.Base, 'PUT', { userId, isActive, roles });
        return data;
    }
    EditMany = async (usersToEdit: typeUser[], usersToAdd: typeUser[], usersToDelete: typeUser[]) => {
        const data = await api.SendRequest(this.Base, 'PUT', { usersToAdd, usersToDelete, usersToEdit });
        return data;
    }
    GetAll = async () => {
        const data = await api.SendRequest<{ users: typeUser[]; }>(`${this.Base}/all`, 'GET');
        return data;
    }
    GetEmailByEmailLink = async (congregation: string, id: string) => {
        const data = await api.SendRequest<{ email: string; }>(`${this.Base}/recovery?id=${id}&team=${congregation}`, 'GET');
        return data;
    }
    GetPopulatedUser = async (userId: number) => {
        const data = await api.SendRequest<{ user: typeUser | null; }>(`${this.Base}?userId=${userId}`, 'GET');
        return data;
    }
    GetPreachingGroups = async () => {
        const data = await api.SendRequest<{ preachingGroups: typePreachingGroup[]; }>(`${this.Base}/group`, 'GET');
        return data;
    }
    GetUserByToken = async (): Promise<{ user: typeUser | null; config: typeConfigDoc | null; logout: boolean; }> => {  // do not use typeResponseData here
        const response = await api.SendRequest<{ config: typeConfigDoc | null; user: typeUser | null; success: boolean; status?: number; }>(this.Base, 'GET');
        if (!response) {
            return {
                user: null,
                config: null,
                logout: false
            };
        }
        const config = response.data?.config ?? unauthenticatedConfig;
        if (!response.success || !response.data?.user) {
            if (response.status !== 500) {
                localStorageHelper.RemoveAuthToken();
                sse.reconnect();
            }
            return {
                user: null,
                config,
                logout: response.status !== 500
            };
        }
        localStorageHelper.SetUser(response.data.user);
        return {
            user: response.data.user,
            config: response.data.config,
            logout: false
        };
    }
    Login = async (email: string, password: string, recaptchaToken: string) => {
        return api.SendRequest(`${this.Base}/token`, 'POST', { email, password }, recaptchaToken);
    }
    LoginByPhone = async (tokenId: string, recaptchaToken: string) => {
        return api.SendRequest(`${this.Base}/token/phone`, 'POST', { tokenId }, recaptchaToken);
    }
    LogoutAll = async () => {
        return api.SendRequest(`${this.Base}/token`, 'DELETE');
    }
    Logout = async () => {
        localStorageHelper.RemoveAuthToken();
        sse.reconnect();
    }
    Register = async (team: number, email: string, invitationId: string, password: string, recaptchaToken: string) => {
        return api.SendRequest(this.Base, 'POST', { email, invitationId, password, team }, recaptchaToken);
    }
    RegisterAdmins = async (email: string, password: string) => {
        return api.SendRequest(this.Base, 'POST', { email, password });
    }
    SendEmailOTP = async (email: string, recaptchaToken: string) => {
        return api.SendRequest(`${this.Base}/token/email`, 'POST', { email }, recaptchaToken);
    }
    SendLinkToRecoverAccount = async (email: string) => {
        return api.SendRequest(this.Base, 'PATCH', { email });
    }
    SetPersonalData = async (userId: number, name: string, lastName: string, phoneNumber: string) => {
        const data = await api.SendRequest(`${this.Base}/personal-data`, 'PUT', { lastName, name, phoneNumber, userId });
        return data;
    }
    SetPreachingGroups = async (groups: typePreachingGroup[]) => {
        const data = await api.SendRequest(`${this.Base}/group`, 'POST', { groups });
        return data;
    }
    // PASSKEY
    PasskeyStartRegistration = async () => {
        const data = await api.SendRequest<{ passkeyChallengeObject: PublicKeyCredentialCreationOptionsJSON; }>(`${this.Base}/passkey-registration`, 'GET');
        return data;
    }
    PasskeyConfirmRegistration = async (attestationResponse: RegistrationResponseJSON, challenge: string) => {
        const data = await api.SendRequest(`${this.Base}/passkey-registration`, 'POST', { attestationResponse, challenge });
        return data;
    }
    PasskeyStartLoginWith = async () => {
        return api.SendRequest<{ passkeyChallengeObject: PublicKeyCredentialCreationOptionsJSON; }>(`${this.Base}/passkey-authentication`, 'GET');
    }
    PasskeyAuthenticate = async (challenge: string, assertionResponse: AuthenticationResponseJSON) => {
        return api.SendRequest(`${this.Base}/passkey-authentication`, 'POST', { assertionResponse, challenge });
    }
    RevokePasskey = async (passkeyId: number) => {
        const data = await api.SendRequest(`${this.Base}/passkey`, 'DELETE', { passkeyId });
        return data;
    }
}

export const userServices = new UserServices();
